import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { canReadOrgPlanningReview, orgPlanningHeadsKey, orgPlanningReviewKey } from '../hooks/orgPlanningReviewKey';
import { useOrgPlanningReviewScope } from '../hooks/useOrgPlanningReviewScope';
import { useVirtualizer } from '@tanstack/react-virtual';
import type Konva from 'konva';
import { CalendarDays, GripVertical, Loader2, Search, Users, X } from 'lucide-react';
import type { CommandManager } from '../../../core/commands/CommandManager';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';
import { cn } from '../../../lib/utils';
import { useConfirmationDialog } from '../../../components/ui/use-confirmation-dialog';
import { useDiagramStore } from '../../../stores/diagramStore';
import { useMultiCanvasViewStore } from '../../../stores/multiCanvasViewStore';
import { useOrgChartViewStore } from '../../../stores/orgchartViewStore';
import { PositionStaffingCommand } from '../commands/PositionStaffingCommand';
import { PositionStructureCommand } from '../commands/PositionStructureCommand';
import { PositionScenarioTransitionCommand, type PositionScenarioTransition } from '../commands/PositionScenarioTransitionCommand';
import { usePositionStaffingCommands, type PositionStaffingOperationNotice } from '../hooks/usePositionStaffingCommands';
import { usePositionStructureCommands } from '../hooks/usePositionStructureCommands';
import { usePositionScenarioCommands } from '../hooks/usePositionScenarioCommands';
import { useOrgPlanningScenarioProjection } from '../hooks/useOrgPlanningScenarioProjection';
import { isStaleOrgScenario } from '../services/atlasProjectionContext';
import type { OrgModelProjection, PositionAssignmentType } from '../models/OrgModelProjection';
import type { PositionChartGroupingPolicy } from '../models/PositionChartGrouping';
import type { PositionStaffingIntent } from '../models/PositionStaffingIntent';
import type { PositionStructureIntent } from '../models/PositionStructureIntent';
import type { OrgPlanningScenarioReview } from '../models/OrgPlanningScenarioReview';
import { resolvePositionStaffingDrop, type PositionStaffingDragPayload } from '../services/positionStaffingDrop';
import {
  positionStaffingOccupancyAccessibleLabel,
  type PositionStaffingCardOccupant,
} from './positionStaffingCard';
import {
  positionScenarioEffectiveAt,
  positionScenarioOptions,
  selectedPositionScenario,
  type PositionScenarioOption,
} from '../services/positionScenarioSelection';
import { usePositionStaffingDragStore } from '../stores/positionStaffingDragStore';
import {
  buildPositionStaffingCandidates,
  buildPositionStaffingDestinations,
  correlateStaffingCandidate,
  groupPositionStaffingCandidates,
  staffingCandidatePositionId,
  type PositionStaffingCandidate,
  type PositionStaffingGroupBy,
} from '../services/positionStaffingWorkbench';
import {
  positionChartGroupTargetPositionId,
  positionChartSeatLabel,
  projectPositionChartGroups,
  resolveSelectedPositionChartGroup,
} from '../services/positionChartGrouping';
import { PositionChartGroupingSettings } from './PositionChartGroupingSettings';
import { PositionChartPropertiesPanel } from './PositionChartPropertiesPanel';
import { useOrgPlanningScenarioStore } from '../stores/orgPlanningScenarioStore';
import { useAtlasAccessSummary } from '../hooks/useAtlasAccessSummary';
import { useAtlasPeopleSearch } from '../hooks/useAtlasPeopleSearch';
import { openAtlasChanges } from '../stores/atlasChangesStore';

const assignmentTypes = ['primary', 'acting', 'interim', 'shared'] as const;

export const PositionStaffingDrawer: React.FC<{
  folderId: string;
  diagramId: string;
  model: OrgModelProjection;
  groupingPolicy: PositionChartGroupingPolicy;
  groupingSaving: boolean;
  groupingError: unknown;
  onSaveGrouping: (policy: PositionChartGroupingPolicy) => Promise<unknown>;
  canWrite: boolean;
  canUseScenarios: boolean;
  operational?: boolean;
  canExecuteOperationalChange?: boolean;
  controlsAccessMessage?: string | null;
  commandManager: CommandManager;
  stageRef?: React.RefObject<Konva.Stage | null>;
  canvasContainerRef?: React.RefObject<HTMLDivElement | null>;
  presentation?: 'overlay' | 'pane';
  onClose?: () => void;
  onManageSuccession?: (positionId: string) => void;
}> = ({
  folderId,
  diagramId,
  model: liveModel,
  groupingPolicy,
  groupingSaving,
  groupingError,
  onSaveGrouping,
  canWrite,
  canUseScenarios,
  operational = false,
  canExecuteOperationalChange = false,
  controlsAccessMessage = null,
  commandManager,
  stageRef,
  canvasContainerRef,
  presentation = 'overlay',
  onClose,
  onManageSuccession,
}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const atlasAccess = useAtlasAccessSummary({ folderId, diagramId });
  const reviewScope = useOrgPlanningReviewScope(folderId, diagramId);
  const canEditPositions = atlasAccess.actionState('editPositions').state === 'enabled';
  const canUseNamedScenarios = canUseScenarios
    && atlasAccess.actionState('applyScenario').state === 'enabled';
  const canViewSuccession = atlasAccess.actionState('viewPlanning').state === 'enabled';
  const { requestConfirmation, confirmationDialog } = useConfirmationDialog();
  const [search, setSearch] = React.useState('');
  const [groupBy, setGroupBy] = React.useState<PositionStaffingGroupBy>('level');
  const [effectiveDate, setEffectiveDate] = React.useState(() => new Date().toISOString().slice(0, 10));
  const [scenarioName, setScenarioName] = React.useState('Position staffing scenario');
  const selectedScenario = useOrgPlanningScenarioStore((state) => state.selectedByDiagram[diagramId]);
  const selectScenario = useOrgPlanningScenarioStore((state) => state.select);
  const selectedScenarioId: 'new' | string = selectedScenario?.headChangeSetId ?? 'new';
  const scenarioProjection = useOrgPlanningScenarioProjection({
    folderId,
    diagramId,
    projectionKind: 'position',
    enabled: true,
  });
  const mode = scenarioProjection.mode;
  const setMode = (next: 'live' | 'scenario') =>
    useOrgPlanningScenarioStore.getState().setMode(diagramId, next);
  const model = mode === 'scenario' && selectedScenario && scenarioProjection.data
    ? scenarioProjection.data.model
    : liveModel;
  const [notice, setNotice] = React.useState<PositionStaffingOperationNotice>({ state: 'idle', message: '' });
  const [newPositionTitle, setNewPositionTitle] = React.useState('');
  const [managerPositionId, setManagerPositionId] = React.useState('');
  const [activePositionId, setActivePositionId] = React.useState<string | null>(null);
  const [movingAssignmentId, setMovingAssignmentId] = React.useState<string | null>(null);
  const canvasDragEnabled = presentation === 'overlay' && Boolean(stageRef && canvasContainerRef);
  const selectedIds = useDiagramStore((state) => state.selectedIds);
  const setSelected = useDiagramStore((state) => state.setSelected);
  const requestCenterOnNode = useOrgChartViewStore((state) => state.requestCenterOnNode);
  const multiViewSelection = useMultiCanvasViewStore((state) => state.selection);
  const setMultiViewSelection = useMultiCanvasViewStore((state) => state.setSelection);
  const positionById = React.useMemo(
    () => new Map(model.positions.map((position) => [position.id, position])),
    [model.positions],
  );
  const positionStateById = React.useMemo(
    () => new Map(model.positionState.map((position) => [position.positionId, position])),
    [model.positionState],
  );
  const positionGroups = React.useMemo(
    () => projectPositionChartGroups(model, groupingPolicy),
    [groupingPolicy, model],
  );
  const managerByPositionId = React.useMemo(
    () => new Map(
      model.relationships
        .filter((relationship) => relationship.predicate === 'reports_to')
        .map((relationship) => [relationship.subjectRef, relationship.objectRef]),
    ),
    [model.relationships],
  );
  const selectedGroup = React.useMemo(
    () => selectedIds.size === 0
      ? null
      : resolveSelectedPositionChartGroup(positionGroups, selectedIds, activePositionId),
    [activePositionId, positionGroups, selectedIds],
  );
  React.useEffect(() => {
    if (!selectedGroup || selectedIds.has(selectedGroup.id)) return;
    setSelected(new Set([selectedGroup.id]));
  }, [selectedGroup, selectedIds, setSelected]);
  React.useEffect(() => {
    if (!selectedGroup) {
      setActivePositionId(null);
      return;
    }
    setActivePositionId((current) => {
      if (current && selectedGroup.positionIds.includes(current)) return current;
      return positionChartGroupTargetPositionId(selectedGroup, positionStateById)
        ?? selectedGroup.positionIds[0]
        ?? null;
    });
  }, [positionStateById, selectedGroup]);
  const selectedPositionId = activePositionId
    && selectedGroup?.positionIds.includes(activePositionId)
    ? activePositionId
    : null;
  const selectedPosition = selectedPositionId ? positionById.get(selectedPositionId) ?? null : null;
  const selectedState = selectedPositionId ? positionStateById.get(selectedPositionId) ?? null : null;
  const targetPositionId = positionChartGroupTargetPositionId(
    selectedGroup,
    positionStateById,
    selectedPositionId,
  );
  const targetState = targetPositionId ? positionStateById.get(targetPositionId) ?? null : null;
  const positionOptions = React.useMemo(() => {
    // A title repeats across departments far more often than not — this folder
    // alone has six "Software Engineer" groups. Listing them as six identical
    // rows makes the picker a guess, so qualify a label with whatever
    // distinguishes it, but only where it actually repeats: adding
    // "· Engineering" to a title that appears once is noise.
    const countBy = (labels: readonly string[]) => {
      const counts = new Map<string, number>();
      for (const label of labels) counts.set(label, (counts.get(label) ?? 0) + 1);
      return counts;
    };
    const base = positionGroups.groups.map((group) => group.label);
    const baseCounts = countBy(base);

    // Pass one: qualify by whatever attribute distinguishes the seat.
    const qualified = positionGroups.groups.map((group, index) => {
      if ((baseCounts.get(base[index]) ?? 0) <= 1) return base[index];
      const attribute = group.department ?? group.level ?? group.location;
      return attribute ? `${base[index]} · ${attribute}` : base[index];
    });
    const qualifiedCounts = countBy(qualified);

    // Pass two: seats that share a title *and* a department are still
    // indistinguishable — four "Account Executive · Sales" rows are no more
    // choosable than four bare ones. Fall back to who sits in the seat, which
    // is the thing a person actually recognises, and only then to a seat code.
    return positionGroups.groups.map((group, index) => {
      const label = qualified[index];
      const ambiguous = (qualifiedCounts.get(label) ?? 0) > 1;
      const occupant = ambiguous
        ? group.occupants[0]?.displayLabel ?? (group.vacantCount ? 'Vacant' : null)
        : null;
      const seatCode = ambiguous && !occupant
        ? positionChartSeatLabel(group.positions[0])
        : null;
      const filled = group.approvedCount > 1
        ? ` · ${group.staffedCount ?? '—'}/${group.approvedCount} filled`
        : '';
      return (
        <option key={group.id} value={group.id}>
          {label}{occupant ? ` · ${occupant}` : ''}{seatCode ? ` · ${seatCode}` : ''}{filled}
        </option>
      );
    });
  }, [positionGroups.groups]);
  // Staffing availability is governed by the predicate-specific occupancy
  // projection. Unrelated entity-property redaction must not disable a visible,
  // complete occupancy lane.
  const occupancyComplete = model.occupancyVisibility === 'visible';
  const nativePayloadRef = React.useRef<PositionStaffingDragPayload | null>(null);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const drawerRef = React.useRef<HTMLElement>(null);
  const moveButtonRefs = React.useRef(new Map<string, HTMLButtonElement>());
  const dragFocusRef = React.useRef<HTMLElement | null>(null);
  const setOperationalChangeSetId = React.useCallback((head: string) => {
    openAtlasChanges(folderId, diagramId, head);
  }, [folderId, diagramId]);
  const useOperationalRoute = operational && mode === 'live';
  const adoptStagedChange = React.useCallback((changeSetId: string, scenarioId?: string | null) => {
    if (operational && mode === 'live') {
      setOperationalChangeSetId(changeSetId);
      return;
    }
    if (scenarioId) selectScenario(diagramId, { scenarioId, headChangeSetId: changeSetId });
  }, [diagramId, mode, operational, selectScenario, setOperationalChangeSetId]);
  const { createServerPort } = usePositionStaffingCommands({
    folderId, diagramId, onNotice: setNotice, onScenarioChangeSet: adoptStagedChange,
    operational: useOperationalRoute,
  });
  const structureCommands = usePositionStructureCommands({
    folderId, diagramId, onNotice: setNotice, onScenarioChangeSet: adoptStagedChange,
    operational: useOperationalRoute,
  });
  const scenarioCommands = usePositionScenarioCommands({
    folderId,
    diagramId,
    onNotice: setNotice,
    onScenarioChangeSet: (changeSetId) => {
      if (!changeSetId) selectScenario(diagramId, null);
      else if (selectedScenario) selectScenario(diagramId, {
        ...selectedScenario,
        headChangeSetId: changeSetId,
      });
    },
  });

  React.useEffect(() => {
    setManagerPositionId(selectedPositionId ? managerByPositionId.get(selectedPositionId) ?? '' : '');
  }, [managerByPositionId, selectedPositionId]);

  const people = useAtlasPeopleSearch({
    folderId,
    diagramId,
    search,
    enabled: true,
  });
  const rows = people.rows;
  const candidates = React.useMemo(
    () => buildPositionStaffingCandidates(rows, model),
    [model, rows],
  );
  const candidateGroups = React.useMemo(
    () => groupPositionStaffingCandidates(candidates, groupBy),
    [candidates, groupBy],
  );
  const candidateByPersonId = React.useMemo(
    () => new Map(candidates.map((candidate) => [candidate.row.personId, candidate])),
    [candidates],
  );
  const selectedCandidate = React.useMemo(
    () => multiViewSelection?.entityKind === 'person'
      && typeof multiViewSelection.entityId === 'string'
      ? candidateByPersonId.get(multiViewSelection.entityId) ?? null
      : null,
    [candidateByPersonId, multiViewSelection?.entityId, multiViewSelection?.entityKind],
  );
  const staffingDestinations = React.useMemo(
    () => buildPositionStaffingDestinations(model, selectedPositionId),
    [model, selectedPositionId],
  );
  const selectedCandidateCorrelation = React.useMemo(
    () => selectedCandidate
      ? correlateStaffingCandidate(selectedCandidate, selectedPositionId, model)
      : null,
    [model, selectedCandidate, selectedPositionId],
  );
  const selectCandidate = React.useCallback((candidate: PositionStaffingCandidate) => {
    setMultiViewSelection({
      entityId: candidate.row.personId,
      entityKind: 'person',
      entityDomain: 'atlas',
      entityLabel: candidate.row.displayLabel,
      sourcePaneId: 'atlas.position-staffing-people',
      syncEnabled: true,
      resolved: true,
    });
    const positionId = staffingCandidatePositionId(candidate);
    if (!positionId) return;
    const group = positionGroups.groupByPositionId.get(positionId);
    if (!group) return;
    setActivePositionId(positionId);
    setSelected(new Set([group.id]));
    requestCenterOnNode(group.id);
  }, [positionGroups.groupByPositionId, requestCenterOnNode, setMultiViewSelection, setSelected]);
  React.useEffect(() => {
    if (people.data && rows.length === 0 && people.hasNextPage && !people.isFetchingNextPage) {
      void people.fetchNextPage();
    }
  }, [people, rows.length]);
  React.useEffect(() => {
    if (!people.deferredSearch || !people.isSuccess) return;
    const frame = window.requestAnimationFrame(() => {
      // A new query always begins at its first result. The selected-position
      // detail stack is height-bounded below so this roster remains visible.
      scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [people.deferredSearch, people.dataUpdatedAt, people.isSuccess, selectedPositionId]);
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 54,
    overscan: 8,
  });

  const scenarios = useQuery({
    queryKey: orgPlanningHeadsKey(reviewScope),
    enabled: canUseScenarios && canReadOrgPlanningReview(reviewScope),
    queryFn: async ({ signal }) => {
      const response = await authenticatedFetch(`/api/folders/${folderId}/diagrams/${diagramId}/atlas/organization-plans`, { signal });
      if (!response.ok) throw new Error(`Scenario lookup failed (${response.status})`);
      return response.json() as Promise<PositionScenarioOption[]>;
    },
  });
  const positionScenarios = React.useMemo(
    () => positionScenarioOptions(scenarios.data),
    [scenarios.data],
  );
  const activeScenario = selectedPositionScenario(positionScenarios, selectedScenarioId);
  const scenarioValidationError = React.useMemo(() => {
    if (mode !== 'scenario' || activeScenario) return null;
    if (!scenarioName.trim()) return 'Enter a scenario name.';
    if (scenarioName.trim().length > 120) return 'Scenario names must be 120 characters or fewer.';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(effectiveDate)
      || Number.isNaN(Date.parse(`${effectiveDate}T00:00:00.000Z`))) {
      return 'Choose a valid effective date.';
    }
    return null;
  }, [activeScenario, effectiveDate, mode, scenarioName]);
  React.useEffect(() => {
    if (!activeScenario) return;
    setScenarioName(activeScenario.title);
    if (activeScenario.effectiveAt) setEffectiveDate(activeScenario.effectiveAt.slice(0, 10));
  }, [activeScenario]);
  const reviewHead = activeScenario?.headChangeSetId ?? activeScenario?.id ?? null;
  const scenarioReview = useQuery({
    queryKey: orgPlanningReviewKey(reviewScope, reviewHead),
    enabled: mode === 'scenario' && Boolean(reviewHead) && canReadOrgPlanningReview(reviewScope),
    queryFn: async ({ signal }) => {
      if (!reviewHead) throw new Error('No active scenario');
      const response = await authenticatedFetch(`/api/folders/${folderId}/diagrams/${diagramId}/atlas/organization-plans/${reviewHead}/review`, { signal });
      if (!response.ok) throw new Error(`Scenario review failed (${response.status})`);
      return response.json() as Promise<OrgPlanningScenarioReview>;
    },
  });
  const restoreFocus = React.useCallback((target: Element | null) => {
    window.requestAnimationFrame(() => {
      if (target instanceof HTMLElement && target.isConnected) target.focus();
      else drawerRef.current?.querySelector<HTMLElement>('[data-position-staffing-focus-fallback="true"]')?.focus();
    });
  }, []);

  const runScenarioTransition = React.useCallback(async (action: PositionScenarioTransition) => {
    if (!activeScenario) return;
    const focusTarget = document.activeElement;
    usePositionStaffingDragStore.getState().setInteractionLocked(true);
    try {
      await commandManager.execute(new PositionScenarioTransitionCommand({
        action,
        canUseScenarios,
        serverPort: scenarioCommands.createServerPort(activeScenario.id, action),
      }));
    } catch {
      // The server port presents a typed, non-sensitive notice.
    } finally {
      usePositionStaffingDragStore.getState().setInteractionLocked(false);
      restoreFocus(focusTarget);
    }
  }, [activeScenario, canUseScenarios, commandManager, restoreFocus, scenarioCommands]);

  const runIntent = React.useCallback(async (partial: StaffingIntentWithoutContext, preferredFocus?: Element | null) => {
    if (scenarioValidationError) {
      setNotice({ state: 'failed', message: scenarioValidationError });
      return;
    }
    const focusTarget = preferredFocus ?? document.activeElement;
    const intent = {
      ...partial,
      intentId: crypto.randomUUID(),
      expectedGraphVersion: model.graphVersion,
      mode,
      ...(mode === 'scenario' ? {
        effectiveAt: positionScenarioEffectiveAt(effectiveDate, activeScenario?.effectiveAt),
        scenarioChangeSetId: activeScenario?.id,
        scenarioId: activeScenario?.scenarioId,
        expectedHeadChangeSetId: activeScenario?.headChangeSetId ?? activeScenario?.id,
        scenarioName,
      } : {}),
    } as PositionStaffingIntent;
    const command = new PositionStaffingCommand({
      intent,
      access: { canWrite, canUseScenarios },
      serverPort: createServerPort(intent),
      skipHistory: useOperationalRoute,
    });
    usePositionStaffingDragStore.getState().setInteractionLocked(true);
    try {
      await commandManager.execute(command);
    } catch {
      // The server port presents a typed, non-sensitive notice.
    } finally {
      usePositionStaffingDragStore.getState().setInteractionLocked(false);
      restoreFocus(focusTarget);
    }
  }, [activeScenario?.effectiveAt, activeScenario?.headChangeSetId, activeScenario?.id, activeScenario?.scenarioId, canUseScenarios, canWrite, commandManager, createServerPort, effectiveDate, mode, model.graphVersion, restoreFocus, scenarioName, scenarioValidationError, useOperationalRoute]);

  const runStructureIntent = React.useCallback(async (partial: StructureIntentWithoutContext) => {
    if (scenarioValidationError) {
      setNotice({ state: 'failed', message: scenarioValidationError });
      return;
    }
    const focusTarget = document.activeElement;
    const intent = {
      ...partial,
      intentId: crypto.randomUUID(),
      expectedGraphVersion: model.graphVersion,
      mode,
      ...(mode === 'scenario' ? {
        effectiveAt: positionScenarioEffectiveAt(effectiveDate, activeScenario?.effectiveAt),
        scenarioChangeSetId: activeScenario?.id,
        scenarioId: activeScenario?.scenarioId,
        expectedHeadChangeSetId: activeScenario?.headChangeSetId ?? activeScenario?.id,
        scenarioName,
      } : {}),
    } as PositionStructureIntent;
    try {
      await commandManager.execute(new PositionStructureCommand({
        intent,
        access: { canWrite, canUseScenarios },
        serverPort: structureCommands.createServerPort(intent),
        skipHistory: useOperationalRoute,
      }));
    } catch {
      // The server port presents a typed, non-sensitive notice.
    } finally {
      restoreFocus(focusTarget);
    }
  }, [activeScenario?.effectiveAt, activeScenario?.headChangeSetId, activeScenario?.id, activeScenario?.scenarioId, canUseScenarios, canWrite, commandManager, effectiveDate, mode, model.graphVersion, restoreFocus, scenarioName, scenarioValidationError, structureCommands, useOperationalRoute]);

  const resolveDrop = React.useCallback(async (payload: PositionStaffingDragPayload, hit: StaffingHit) => {
    const targetState = hit.positionId
      ? positionStateById.get(hit.positionId)
      : undefined;
    const resolution = resolvePositionStaffingDrop({
      payload,
      targetPositionId: hit.positionId,
      droppedOnBody: hit.body,
      canWrite,
      occupancyComplete,
      targetHasPrimaryOccupant: Boolean(targetState?.primaryOccupantPersonId),
      unassignZone: hit.unassign,
    });
    if (resolution.outcome === 'assign') {
      await runIntent({ kind: 'assign', personId: resolution.personId, targetPositionId: resolution.targetPositionId, assignmentType: 'primary' });
    } else if (resolution.outcome === 'move') {
      await runIntent({ kind: 'move', sourceAssignmentId: resolution.assignmentId, targetPositionId: resolution.targetPositionId });
    } else if (resolution.outcome === 'unassign') {
      await runIntent({ kind: 'unassign', sourceAssignmentId: resolution.assignmentId });
    } else if (resolution.outcome === 'noop') {
      setNotice({ state: 'idle', message: 'That person is already in this position.' });
    } else if (resolution.reason === 'outside_body') {
      setNotice({ state: 'idle', message: '' });
    } else {
      setNotice({ state: resolution.reason === 'target_primary_occupied' ? 'conflict' : 'failed', message: dropErrorMessage(resolution.reason) });
    }
  }, [canWrite, occupancyComplete, positionStateById, runIntent]);

  const previewDrop = React.useCallback((payload: PositionStaffingDragPayload, hit: StaffingHit) => {
    const targetState = hit.positionId
      ? positionStateById.get(hit.positionId)
      : undefined;
    const resolution = resolvePositionStaffingDrop({
      payload,
      targetPositionId: hit.positionId,
      droppedOnBody: hit.body,
      canWrite,
      occupancyComplete,
      targetHasPrimaryOccupant: Boolean(targetState?.primaryOccupantPersonId),
      unassignZone: hit.unassign,
    });
    return resolution.outcome === 'assign' || resolution.outcome === 'move' || resolution.outcome === 'unassign'
      ? 'valid' as const
      : resolution.outcome === 'noop' ? 'noop' as const : 'invalid' as const;
  }, [canWrite, occupancyComplete, positionStateById]);

  React.useEffect(() => {
    if (!canvasDragEnabled || !canvasContainerRef || !stageRef) return;
    const container = canvasContainerRef.current;
    if (!container) return;
    const dragOver = (event: DragEvent) => {
      if (!nativePayloadRef.current) return;
      const hit = staffingHit(stageRef.current, event.clientX, event.clientY);
      if (hit.body) event.preventDefault();
      usePositionStaffingDragStore.getState().move(
        'native',
        { x: event.clientX, y: event.clientY },
        hit.body ? hit.positionId : null,
        previewDrop(nativePayloadRef.current, hit),
      );
    };
    const drop = (event: DragEvent) => {
      const payload = nativePayloadRef.current;
      if (!payload) return;
      event.preventDefault();
      const hit = staffingHit(stageRef.current, event.clientX, event.clientY);
      nativePayloadRef.current = null;
      usePositionStaffingDragStore.getState().clear('native');
      void resolveDrop(payload, hit);
    };
    container.addEventListener('dragover', dragOver);
    container.addEventListener('drop', drop);
    return () => {
      container.removeEventListener('dragover', dragOver);
      container.removeEventListener('drop', drop);
    };
  }, [canvasContainerRef, canvasDragEnabled, previewDrop, resolveDrop, stageRef]);

  React.useEffect(() => {
    if (!canvasDragEnabled || !stageRef) return;
    const move = (event: MouseEvent) => {
      const drag = usePositionStaffingDragStore.getState();
      if (!drag.payload) return;
      const hit = staffingHit(stageRef.current, event.clientX, event.clientY);
      drag.move('mouse', { x: event.clientX, y: event.clientY }, hit.body ? hit.positionId : null, previewDrop(drag.payload, hit));
    };
    const up = (event: MouseEvent) => {
      const drag = usePositionStaffingDragStore.getState();
      if (!drag.payload || !drag.origin) return;
      const distance = Math.hypot(event.clientX - drag.origin.x, event.clientY - drag.origin.y);
      const payload = drag.payload;
      const unassign = payload.kind === 'assignment'
        && Boolean(document.elementFromPoint(event.clientX, event.clientY)?.closest('[data-position-staffing-unassign="true"]'));
      const hit = staffingHit(stageRef.current, event.clientX, event.clientY);
      drag.clear('mouse');
      if (distance < 5) return;
      void resolveDrop(payload, { ...hit, unassign });
    };
    const touchMove = (event: TouchEvent) => {
      const drag = usePositionStaffingDragStore.getState();
      const touch = [...event.touches].find((candidate) => candidate.identifier === drag.pointerId);
      if (!touch || !drag.payload) return;
      event.preventDefault();
      const hit = staffingHit(stageRef.current, touch.clientX, touch.clientY);
      drag.move(touch.identifier, { x: touch.clientX, y: touch.clientY }, hit.body ? hit.positionId : null, previewDrop(drag.payload, hit));
    };
    const touchEnd = (event: TouchEvent) => {
      const drag = usePositionStaffingDragStore.getState();
      const touch = [...event.changedTouches].find((candidate) => candidate.identifier === drag.pointerId);
      if (!touch || !drag.payload || !drag.origin) return;
      const distance = Math.hypot(touch.clientX - drag.origin.x, touch.clientY - drag.origin.y);
      const payload = drag.payload;
      const unassign = payload.kind === 'assignment'
        && Boolean(document.elementFromPoint(touch.clientX, touch.clientY)?.closest('[data-position-staffing-unassign="true"]'));
      const hit = staffingHit(stageRef.current, touch.clientX, touch.clientY);
      drag.clear(touch.identifier);
      if (distance < 5) return;
      void resolveDrop(payload, { ...hit, unassign });
    };
    const cancel = (event: KeyboardEvent | Event) => {
      if (event instanceof KeyboardEvent && event.key !== 'Escape') return;
      const drag = usePositionStaffingDragStore.getState();
      if (drag.payload) setNotice({ state: 'idle', message: 'Staffing drag cancelled.' });
      drag.clear();
      restoreFocus(dragFocusRef.current);
      dragFocusRef.current = null;
    };
    const touchCancel = (event: TouchEvent) => {
      const drag = usePositionStaffingDragStore.getState();
      const touch = [...event.changedTouches].find((candidate) => candidate.identifier === drag.pointerId);
      if (touch) {
        drag.clear(touch.identifier);
        setNotice({ state: 'idle', message: 'Staffing drag cancelled.' });
        restoreFocus(dragFocusRef.current);
        dragFocusRef.current = null;
      }
    };
    const pointerMove = (event: PointerEvent) => {
      const drag = usePositionStaffingDragStore.getState();
      if (!drag.payload || drag.pointerId !== event.pointerId) return;
      event.preventDefault();
      const hit = staffingHit(stageRef.current, event.clientX, event.clientY);
      drag.move(event.pointerId, { x: event.clientX, y: event.clientY }, hit.body ? hit.positionId : null, previewDrop(drag.payload, hit));
    };
    const pointerUp = (event: PointerEvent) => {
      const drag = usePositionStaffingDragStore.getState();
      if (!drag.payload || !drag.origin || drag.pointerId !== event.pointerId) return;
      const payload = drag.payload;
      const distance = Math.hypot(event.clientX - drag.origin.x, event.clientY - drag.origin.y);
      const unassign = payload.kind === 'assignment'
        && Boolean(document.elementFromPoint(event.clientX, event.clientY)?.closest('[data-position-staffing-unassign="true"]'));
      const hit = staffingHit(stageRef.current, event.clientX, event.clientY);
      drag.clear(event.pointerId);
      dragFocusRef.current = null;
      if (distance < 5) return;
      void resolveDrop(payload, { ...hit, unassign });
    };
    const pointerCancel = (event: PointerEvent) => {
      const drag = usePositionStaffingDragStore.getState();
      if (drag.pointerId !== event.pointerId) return;
      drag.clear(event.pointerId);
      setNotice({ state: 'idle', message: 'Staffing drag cancelled.' });
      restoreFocus(dragFocusRef.current);
      dragFocusRef.current = null;
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    window.addEventListener('touchmove', touchMove, { passive: false });
    window.addEventListener('touchend', touchEnd);
    window.addEventListener('touchcancel', touchCancel);
    window.addEventListener('pointermove', pointerMove, { passive: false });
    window.addEventListener('pointerup', pointerUp);
    window.addEventListener('pointercancel', pointerCancel);
    window.addEventListener('keydown', cancel);
    window.addEventListener('blur', cancel);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchmove', touchMove);
      window.removeEventListener('touchend', touchEnd);
      window.removeEventListener('touchcancel', touchCancel);
      window.removeEventListener('pointermove', pointerMove);
      window.removeEventListener('pointerup', pointerUp);
      window.removeEventListener('pointercancel', pointerCancel);
      window.removeEventListener('keydown', cancel);
      window.removeEventListener('blur', cancel);
    };
  }, [canvasDragEnabled, previewDrop, resolveDrop, restoreFocus, runIntent, stageRef]);

  const dragPointer = usePositionStaffingDragStore((state) => state.pointer);
  const dragging = usePositionStaffingDragStore((state) => state.payload);
  const hoveredPositionId = usePositionStaffingDragStore((state) => state.hoveredPositionId);
  const hoveredState = hoveredPositionId
    ? positionStateById.get(hoveredPositionId)
    : undefined;
  const dragMessage = !hoveredPositionId
    ? dragging?.kind === 'assignment' ? 'Move occupant' : 'Assign person'
    : hoveredState?.primaryOccupantPersonId ? 'Primary position is filled'
      : dragging?.kind === 'assignment' && dragging.sourcePositionId === hoveredPositionId ? 'Already in this position'
        : dragging?.kind === 'assignment' ? 'Move here' : 'Assign here';
  React.useEffect(() => {
    if (dragging) setNotice({ state: 'idle', message: dragMessage });
  }, [dragMessage, dragging]);
  const selectedLabel = selectedGroup?.label ?? 'Select a position';
  const scenarioProjectionUnavailable = mode === 'scenario'
    && (scenarioProjection.isDiscovering || Boolean(scenarioProjection.discoveryError)
      || (Boolean(selectedScenario) && (!scenarioProjection.isSuccess
        || !activeScenario || activeScenario.id !== selectedScenario?.headChangeSetId)));
  const baseControlsDisabled = !canWrite
    || !canEditPositions
    || (useOperationalRoute && !canExecuteOperationalChange)
    || !occupancyComplete
    || notice.state === 'pending'
    || scenarioCommands.isPending;
  const controlsDisabled = baseControlsDisabled || scenarioProjectionUnavailable;

  return (
    <>
    <aside
      ref={drawerRef}
      tabIndex={-1}
      data-testid="position-staffing-drawer"
      data-presentation={presentation}
      className={cn(
        // `overflow-y-auto` rather than `overflow-hidden`: the inner regions
        // carry the scrolling at normal sizes, but once the header, the capped
        // properties block, the search field and the roster floor exceed the
        // panel there has to be somewhere for the overflow to go. Hidden meant
        // it was simply cut off, with no scrollbar anywhere on the panel.
        'flex min-h-0 flex-col overflow-x-hidden overflow-y-auto bg-white outline-none focus-visible:ring-2 focus-visible:ring-blue-500 [&_button]:min-h-10 [&_input]:min-h-10 [&_select]:min-h-10',
        presentation === 'pane'
          ? 'relative h-full w-full'
          : 'absolute inset-x-2 bottom-2 z-40 max-h-[min(78vh,720px)] rounded-xl border border-slate-200 bg-white/95 shadow-xl backdrop-blur sm:inset-x-auto sm:bottom-auto sm:right-4 sm:top-20 sm:h-[min(720px,calc(100%-7rem))] sm:max-h-none sm:w-[340px]',
      )}
      aria-label="Position staffing"
    >
      <div className="border-b border-slate-200 px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><Users className="h-4 w-4" /> Staffing workbench</div>
            <p className="mt-0.5 truncate text-xs text-slate-500" title={selectedLabel}>{selectedLabel}</p>
          </div>
          <div className="flex items-center gap-1">
            <div data-testid="position-staffing-mode" className="flex rounded-md bg-slate-100 p-0.5 text-xs" aria-label="Staffing mode" role="group">
              <button data-position-staffing-focus-fallback="true" className={`min-h-10 rounded px-2 py-1 focus-visible:ring-2 focus-visible:ring-blue-500 ${mode === 'live' ? 'bg-white shadow-sm' : ''}`} aria-pressed={mode === 'live'} disabled={baseControlsDisabled || scenarioProjection.isPlanningArtifact} title={scenarioProjection.isPlanningArtifact ? 'Apply the reviewed plan to change the live organization.' : undefined} onClick={() => setMode('live')}>Live</button>
              <button className={`min-h-10 rounded px-2 py-1 ${mode === 'scenario' ? 'bg-violet-600 text-white shadow-sm' : ''}`} aria-pressed={mode === 'scenario'} disabled={!canUseNamedScenarios || baseControlsDisabled} title={!canUseNamedScenarios ? 'Atlas Advanced or additional access is required' : undefined} onClick={() => setMode('scenario')}>Scenario</button>
            </div>
            {onClose ? (
              <button
                type="button"
                onClick={onClose}
                className="flex size-10 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                aria-label="Close staffing workbench"
              >
                <X className="size-4" />
              </button>
            ) : null}
          </div>
        </div>
        {/* Naming Move… before a position is chosen described a control that is
            not on screen yet. Lead with the step the reader can actually take,
            and only mention occupant actions once an occupant exists. */}
        <p className="mt-2 text-[11px] leading-4 text-slate-500">
          {!selectedGroup
            ? 'Choose a position to see the people who could fill it.'
            : canvasDragEnabled
              ? 'Drag a person by the grip or use Assign. To move an occupant, drag their grip onto another position card or use Move….'
              : 'Compare the grouped people below, then use Assign. Use Move… for an existing occupant.'}
        </p>
        <label className="mt-3 block text-[11px] font-medium text-slate-600">Target position
          <select
            className="mt-1 min-h-10 w-full rounded border border-slate-200 bg-white px-2 text-xs text-slate-800"
            aria-label="Target position"
            value={selectedGroup?.id ?? ''}
            onChange={(event) => {
              const group = positionGroups.groupById.get(event.target.value);
              setActivePositionId(
                positionChartGroupTargetPositionId(group, positionStateById)
                  ?? group?.positionIds[0]
                  ?? null,
              );
              setSelected(group ? new Set([group.id]) : new Set());
            }}
          >
            <option value="">Choose a position…</option>
            {positionOptions}
          </select>
        </label>
        {mode === 'scenario' && (
          <div className="mt-3 rounded-md border border-violet-200 bg-violet-50 p-2 text-xs text-violet-900">
            <label className="mb-2 flex items-center gap-2">Scenario
              <select data-testid="position-staffing-scenario-select" className="ml-auto w-44 rounded border border-violet-200 bg-white px-1.5 py-1" value={selectedScenarioId} disabled={controlsDisabled} onChange={(event) => {
                const selected = positionScenarios.find((scenario) => scenario.id === event.target.value);
                if (!selected?.scenarioId) selectScenario(diagramId, null);
                else selectScenario(diagramId, {
                  scenarioId: selected.scenarioId,
                  headChangeSetId: selected.headChangeSetId ?? selected.id,
                });
              }}>
                <option value="new">New scenario</option>
                {positionScenarios.map((scenario) => <option key={scenario.id} value={scenario.id}>{scenario.title}</option>)}
              </select>
            </label>
            {scenarios.error && <p className="mb-2 text-red-700" role="alert">Scenarios are unavailable. <button className="min-h-10 underline" onClick={() => void scenarios.refetch()}>Retry</button></p>}
            {selectedScenario && scenarioProjection.isLoading && (
              <p className="mb-2 text-violet-700" role="status">Loading the selected scenario projection…</p>
            )}
            {selectedScenario && scenarioProjection.error && (
              <p className="mb-2 text-red-700" role="alert">
                {isStaleOrgScenario(scenarioProjection.error)
                  ? 'The organization changed. Use Rebase below to update this plan before previewing or applying it.'
                  : <>Scenario projection is unavailable.{' '}
                    <button className="min-h-10 underline" onClick={() => void scenarioProjection.refetch()}>Retry</button>
                  </>}
              </p>
            )}
            <label className="mb-2 flex items-center gap-2">Scenario name
              <input className="ml-auto min-h-10 w-44 rounded border border-violet-200 bg-white px-1.5 py-1 disabled:bg-violet-100" value={scenarioName} maxLength={120} aria-invalid={Boolean(scenarioValidationError?.includes('name'))} disabled={Boolean(activeScenario) || controlsDisabled} onChange={(event) => setScenarioName(event.target.value)} />
            </label>
            <label className="flex items-center gap-2"><CalendarDays className="h-3.5 w-3.5" /> Effective date
              <input className="ml-auto min-h-10 rounded border border-violet-200 bg-white px-1.5 py-1 disabled:bg-violet-100" type="date" value={effectiveDate} aria-invalid={Boolean(scenarioValidationError?.includes('date'))} disabled={Boolean(activeScenario) || controlsDisabled} onChange={(event) => setEffectiveDate(event.target.value)} />
            </label>
            {scenarioValidationError && <p className="mt-2 text-red-700" role="alert">{scenarioValidationError}</p>}
            <div className="mt-2 flex items-center justify-between">
              <span>{activeScenario ? `${scenarioCount(activeScenario.summary)} staged` : 'A draft starts with your next change'}</span>
              {activeScenario && <div className="flex gap-1">
                <button className="min-h-10 rounded bg-white px-2 py-1" disabled={scenarioCommands.isPending} onClick={() => void runScenarioTransition('rebase')}>Rebase</button>
                <button className="min-h-10 rounded bg-white px-2 py-1" disabled={scenarioCommands.isPending} onClick={() => void runScenarioTransition('discard')}>Discard</button>
                <button className="min-h-10 rounded bg-violet-600 px-2 py-1 text-white disabled:opacity-50" disabled={!scenarioReview.data?.canApply || scenarioCommands.isPending} onClick={() => reviewHead && openAtlasChanges(folderId, diagramId, reviewHead)}>Review changes</button>
              </div>}
            </div>
            {activeScenario && (
              <p className="mt-1 text-[10px] leading-4 text-violet-700">
                This diagram · revision {shortRevision(activeScenario.headChangeSetId ?? activeScenario.id)}
                {' · '}shared with diagram collaborators
                {scenarioCategorySummary(activeScenario.summary)
                  ? ` · ${scenarioCategorySummary(activeScenario.summary)}`
                  : ''}
              </p>
            )}
            {activeScenario && (
              <div data-testid="position-staffing-scenario-review" className="mt-2 rounded border border-violet-200 bg-white p-2">
                {scenarioReview.isLoading ? 'Reviewing staged changes…' : scenarioReview.error ? 'Review unavailable.' : scenarioReview.data ? (
                  <>
                    <div className="flex justify-between"><span>{scenarioReview.data.diff.length} normalized changes</span><span>{scenarioReview.data.canApply ? 'Ready to apply' : 'Blocked'}</span></div>
                    <div className="mt-1 text-[10px] text-violet-700">Before {impactCount(scenarioReview.data.impact.before)} · After {impactCount(scenarioReview.data.impact.after)}</div>
                    {scenarioReview.data.rebaseRequired && <div className="mt-1 text-amber-700">The live model changed. Rebase before applying.</div>}
                    {scenarioReview.data.conflicts.length > 0 && <div className="mt-1 text-red-700">{scenarioReview.data.conflicts.length} conflict(s) require attention.</div>}
                  </>
                ) : null}
              </div>
            )}
          </div>
        )}
        {controlsAccessMessage && (
          <div
            className="mt-2 rounded bg-amber-50 p-2 text-xs text-amber-900"
            data-testid="position-staffing-access-message"
            role="status"
          >
            {controlsAccessMessage}
          </div>
        )}
        {!occupancyComplete && <div className="mt-2 rounded bg-amber-50 p-2 text-xs text-amber-900">Staffing controls are disabled because occupancy data is incomplete or hidden.</div>}
        <div data-testid={notice.state === 'conflict' ? 'position-staffing-conflict' : 'position-staffing-status'} className={`mt-2 min-h-5 text-xs ${notice.state === 'conflict' || notice.state === 'failed' ? 'text-red-700' : notice.state === 'indeterminate' ? 'text-amber-700' : 'text-slate-600'}`} aria-live="polite">
          {notice.state === 'pending' && <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />}{notice.message}
        </div>
      </div>

      {confirmationDialog}

      {/* Everything between the header and the roster used to be unscrollable
          fixed content inside an `overflow-hidden` aside, so on a short viewport
          the lower property fields were clipped with no way to reach them. This
          stack now shrinks and scrolls on its own; the roster below keeps its
          own scroll region and still grows into the free space.

          The cap is unconditional. The roster is `flex-1` — flex-basis 0 — so it
          only receives space left over after this block's natural height, and a
          selected position makes that height exceed the panel on any normal
          laptop. The roster then resolved to exactly 0px: fifty Assign buttons
          in the DOM, none of them rendered, so a seat could not be staffed at
          all above ~1000px of viewport. Capping this block leaves the roster a
          share of the panel at every height. */}
      <div className="min-h-0 max-h-[45%] shrink overflow-y-auto">
      <PositionChartPropertiesPanel
        group={selectedGroup}
        activePosition={selectedPosition}
        activeState={selectedState}
        successionState={!canViewSuccession
          ? 'hidden'
          : model.completeness.relationships.complete
            && model.authorization.omittedRelationCount === 0
            ? 'visible'
            : 'unavailable'}
        positions={model.positions}
        managerPositionId={managerPositionId}
        disabled={controlsDisabled}
        onActivePositionChange={setActivePositionId}
        onManagerPositionChange={setManagerPositionId}
        onSaveDetails={(details) => {
          if (!selectedPositionId) return;
          void runStructureIntent({
            kind: 'edit',
            positionId: selectedPositionId,
            ...details,
          });
        }}
        onApplyManager={() => {
          if (!selectedPositionId) return;
          void runStructureIntent({
            kind: 'reparent',
            positionId: selectedPositionId,
            managerPositionId: managerPositionId || null,
          });
        }}
        onAddSeat={() => {
          if (!selectedPositionId) return;
          void runStructureIntent({ kind: 'add_seat', sourcePositionId: selectedPositionId });
        }}
        onRetire={async () => {
          if (!selectedPositionId) return;
          const confirmed = await requestConfirmation({
            title: 'Retire this position?',
            description: `Retiring ${selectedGroup?.label ?? selectedPosition?.label ?? 'this position'} will also retire its connected assignments and reporting lines.`,
            confirmLabel: 'Retire position',
          });
          if (!confirmed) return;
          await runStructureIntent({
            kind: 'retire',
            positionId: selectedPositionId,
            reportingPolicy: 'retire_all',
          });
        }}
        onManageSuccession={onManageSuccession}
      />

      {selectedState && selectedState.occupancyVisibility === 'visible' && selectedState.occupants.length > 0 && (
        <div className="border-b border-slate-200 px-4 py-3">
          <p className="sr-only">
            {positionStaffingOccupancyAccessibleLabel(
              selectedPosition?.label ?? 'Selected position',
              selectedState.occupants as readonly PositionStaffingCardOccupant[],
            )}
          </p>
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Current occupants</div>
          <div className="space-y-2">
            {selectedState.occupants.map((occupant) => (
              <div key={occupant.assignmentId} className="rounded-md border border-slate-200 p-2">
                <div className="flex items-center gap-2">
                  {canvasDragEnabled ? (
                    <button
                      type="button"
                      className="flex h-10 w-10 shrink-0 touch-none items-center justify-center rounded text-slate-400 disabled:text-slate-300"
                      disabled={controlsDisabled}
                      aria-label={`Drag ${occupant.displayLabel} to another position`}
                      onPointerDown={(event) => {
                        if (controlsDisabled) return;
                        event.preventDefault();
                        dragFocusRef.current = event.currentTarget;
                        event.currentTarget.setPointerCapture(event.pointerId);
                        usePositionStaffingDragStore.getState().begin({
                          kind: 'assignment',
                          assignmentId: occupant.assignmentId,
                          personId: occupant.personId,
                          sourcePositionId: selectedPositionId ?? '',
                        }, { x: event.clientX, y: event.clientY }, event.pointerId);
                      }}
                      onLostPointerCapture={(event) => {
                        const drag = usePositionStaffingDragStore.getState();
                        if (drag.pointerId !== event.pointerId) return;
                        drag.clear(event.pointerId);
                        setNotice({ state: 'idle', message: 'Staffing drag cancelled.' });
                        restoreFocus(dragFocusRef.current);
                        dragFocusRef.current = null;
                      }}
                    ><GripVertical className="h-4 w-4" aria-hidden /></button>
                  ) : null}
                  <span className="min-w-0 flex-1 truncate text-xs font-medium">{occupant.displayLabel}</span>
                  <button ref={(element) => { if (element) moveButtonRefs.current.set(occupant.assignmentId, element); else moveButtonRefs.current.delete(occupant.assignmentId); }} className="min-h-10 rounded px-2 text-xs text-blue-700 disabled:text-slate-400" disabled={controlsDisabled} aria-label={`Move ${occupant.displayLabel} from ${selectedPosition?.label ?? 'this position'}`} aria-expanded={movingAssignmentId === occupant.assignmentId} onClick={() => setMovingAssignmentId((current) => current === occupant.assignmentId ? null : occupant.assignmentId)}>Move…</button>
                  <button data-testid={`position-unassign-${occupant.assignmentId}`} className="min-h-10 rounded px-2 text-xs text-red-700 disabled:text-slate-400" disabled={controlsDisabled} aria-label={`Unassign ${occupant.displayLabel} from ${selectedPosition?.label ?? 'this position'}`} onClick={() => void runIntent({ kind: 'unassign', sourceAssignmentId: occupant.assignmentId })}>Unassign</button>
                </div>
                {movingAssignmentId === occupant.assignmentId && (
                  <label className="mt-2 block text-[11px] text-slate-500">Move to position
                    <select
                      autoFocus
                      className="mt-1 min-h-10 w-full rounded border border-slate-200 bg-white px-2 text-xs text-slate-800"
                      aria-label={`Move ${occupant.displayLabel} to position`}
                      defaultValue=""
                      disabled={controlsDisabled}
                      onChange={(event) => {
                        const targetPositionId = event.target.value;
                        if (!targetPositionId) return;
                        const focusTarget = moveButtonRefs.current.get(occupant.assignmentId) ?? drawerRef.current;
                        setMovingAssignmentId(null);
                        void runIntent({ kind: 'move', sourceAssignmentId: occupant.assignmentId, targetPositionId }, focusTarget);
                      }}
                    >
                      <option value="">Choose a position…</option>
                      {staffingDestinations.map((destination) => {
                        const primaryBlocked = occupant.assignmentType === 'primary'
                          && destination.primaryBlocked;
                        return <option key={destination.positionId} value={destination.positionId} disabled={primaryBlocked}>{destination.label}{primaryBlocked ? ' (filled)' : ''}</option>;
                      })}
                    </select>
                  </label>
                )}
                <label className="mt-2 flex items-center gap-2 text-[11px] text-slate-500">Assignment
                  <select aria-label={`Assignment type for ${occupant.displayLabel}`} className="ml-auto min-h-10 rounded border border-slate-200 bg-white px-2 text-xs text-slate-800" disabled={controlsDisabled} value={occupant.assignmentType === 'unknown' ? 'primary' : occupant.assignmentType} onChange={(event) => void runIntent({ kind: 'change_assignment_type', sourceAssignmentId: occupant.assignmentId, assignmentType: event.target.value as KnownAssignmentType })}>
                    {assignmentTypes.map((type) => <option key={type} value={type}>{capitalize(type)}</option>)}
                  </select>
                </label>
                <p className="mt-1 text-[10px] text-slate-400">
                  {canvasDragEnabled
                    ? 'Drag the person grip onto another position card, or use Move… to choose a destination.'
                    : 'Use Move… to choose a destination while keeping the chart and roster linked.'}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      <details className="border-b border-slate-200 px-4 py-2">
        <summary className="cursor-pointer text-xs font-semibold text-slate-700">Position structure</summary>
        <div className="mt-3 space-y-3 pb-2 text-xs">
          <div className="rounded-md border border-slate-200 p-2">
            <label className="block text-[11px] font-medium text-slate-500">Create position</label>
            <div className="mt-1 flex gap-1">
              <input className="min-w-0 flex-1 rounded border border-slate-200 px-2 py-1" value={newPositionTitle} onChange={(event) => setNewPositionTitle(event.target.value)} placeholder="Position title" />
              <button className="rounded bg-blue-600 px-2 py-1 text-white disabled:bg-slate-300" disabled={controlsDisabled || !newPositionTitle.trim()} onClick={() => {
                void runStructureIntent({ kind: 'create', title: newPositionTitle.trim(), managerPositionId: selectedPositionId });
                setNewPositionTitle('');
              }}>Create</button>
            </div>
            <p className="mt-1 text-[10px] text-slate-400">Select a card first to create the new position beneath it, or create a root position.</p>
          </div>
        </div>
      </details>
      <PositionChartGroupingSettings
        policy={groupingPolicy}
        canWrite={canWrite}
        isSaving={groupingSaving}
        error={groupingError}
        onSave={onSaveGrouping}
      />

      {presentation === 'pane' ? (
        <div className="border-b border-slate-200 px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">People explorer</div>
              <p className="mt-0.5 text-[10px] text-slate-400">Grouped from available source fields. No hidden fit score.</p>
            </div>
            <label className="text-[10px] font-medium text-slate-500">
              Group by
              <select
                value={groupBy}
                onChange={(event) => setGroupBy(event.target.value as PositionStaffingGroupBy)}
                className="ml-2 min-h-10 rounded border border-slate-200 bg-white px-2 text-xs text-slate-800"
                aria-label="Group staffing people by"
              >
                <option value="status">Assignment status</option>
                <option value="role">Current role</option>
                <option value="level">Level</option>
                <option value="department">Department</option>
                <option value="experience">Experience</option>
              </select>
            </label>
          </div>
          {selectedCandidate ? (
            <div data-testid="position-staffing-correlation" className="mt-3 rounded-lg border border-blue-200 bg-blue-50/60 p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-xs font-semibold text-slate-900">{selectedCandidate.row.displayLabel}</div>
                  <div className="mt-0.5 truncate text-[10px] text-slate-500">
                    {selectedCandidate.role} · {selectedCandidate.level}
                  </div>
                </div>
                <button
                  type="button"
                  className="shrink-0 rounded-md bg-blue-600 px-2 py-1 text-[11px] font-medium text-white disabled:bg-slate-300"
                  disabled={controlsDisabled
                    || !selectedCandidate.row.assignable
                    || !targetPositionId
                    || Boolean(targetState?.primaryOccupantPersonId)}
                  onClick={() => targetPositionId && void runIntent({
                    kind: 'assign',
                    personId: selectedCandidate.row.personId,
                    targetPositionId,
                    assignmentType: 'primary',
                  })}
                >
                  Assign to selected
                </button>
              </div>
              {selectedPosition ? (
                <div className="mt-2 text-[10px] text-slate-600">
                    Comparing with <span className="font-semibold text-slate-800">{selectedGroup?.label ?? selectedPosition.label}</span>
                </div>
              ) : (
                <div className="mt-2 text-[10px] text-amber-700">Select a position on the chart to compare and assign.</div>
              )}
              {selectedCandidateCorrelation
                && (selectedCandidateCorrelation.matchedRequirements.length > 0
                  || selectedCandidateCorrelation.missingRequirements.length > 0) ? (
                <div className="mt-2 flex flex-wrap gap-1">
                  {selectedCandidateCorrelation.matchedRequirements.map((requirement) => (
                    <span key={`matched:${requirement}`} className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-800">
                      Evidence: {requirement}
                    </span>
                  ))}
                  {selectedCandidateCorrelation.missingRequirements.map((requirement) => (
                    <span key={`missing:${requirement}`} className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">
                      Not evidenced: {requirement}
                    </span>
                  ))}
                </div>
              ) : selectedPosition ? (
                <p className="mt-2 text-[10px] text-slate-500">No explicit skill requirements are available for this position.</p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      </div>

      <div className="relative px-4 py-3">
        <Search className="pointer-events-none absolute left-6 top-5.5 h-3.5 w-3.5 text-slate-400" />
        <input className="w-full rounded-md border border-slate-200 py-1.5 pl-8 pr-2 text-xs outline-none focus:border-blue-500" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search people" aria-label="Search people" />
      </div>
      {/* A floor as well as a share: with the properties block capped the roster
          always receives space, but on a very short panel that share can still
          round down to a couple of rows. The floor keeps it usable, and the
          aside's own overflow handles the rare case where even that will not
          fit. */}
      <div ref={scrollRef} className="min-h-[160px] flex-1 overflow-auto px-2" onScroll={(event) => {
        const element = event.currentTarget;
        if (element.scrollHeight - element.scrollTop - element.clientHeight < 160 && people.hasNextPage && !people.isFetchingNextPage) void people.fetchNextPage();
      }}>
        {people.isLoading ? <div className="p-6 text-center text-xs text-slate-500">Loading people…</div> : people.error ? <div className="p-6 text-center text-xs text-red-700">People are unavailable. <button className="min-h-10 rounded px-2 underline" onClick={() => void people.refetch()}>Retry</button></div> : rows.length === 0 ? <div className="p-6 text-center text-xs text-slate-500">{search.trim() ? 'No people match this search.' : 'No people are available to staff.'}</div> : presentation === 'pane' ? (
          <div className="space-y-3 px-2 pb-4">
            {candidateGroups.map((group) => (
              <section key={group.id} className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50/70">
                <div className="flex items-center justify-between border-b border-slate-200 bg-white px-3 py-2">
                  <h3 className="truncate text-[11px] font-semibold text-slate-800">{group.label}</h3>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">{group.candidates.length}</span>
                </div>
                <div className="space-y-1 p-2">
                  {group.candidates.map((candidate) => (
                    <WorkbenchCandidateRow
                      key={candidate.row.personId}
                      candidate={candidate}
                      selected={selectedCandidate?.row.personId === candidate.row.personId}
                      controlsDisabled={controlsDisabled}
                      targetPositionLabel={selectedGroup?.label ?? null}
                      targetBlocked={!targetPositionId || Boolean(targetState?.primaryOccupantPersonId)}
                      onSelect={() => selectCandidate(candidate)}
                      onAssign={() => targetPositionId && void runIntent({
                        kind: 'assign',
                        personId: candidate.row.personId,
                        targetPositionId,
                        assignmentType: 'primary',
                      })}
                    />
                  ))}
                </div>
              </section>
            ))}
            {people.hasNextPage ? (
              <button
                type="button"
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:text-slate-400"
                disabled={people.isFetchingNextPage}
                onClick={() => void people.fetchNextPage()}
              >
                {people.isFetchingNextPage ? 'Loading more people…' : 'Load more people'}
              </button>
            ) : null}
          </div>
        ) : (
          <div className="relative" style={{ height: virtualizer.getTotalSize() }}>
            {virtualizer.getVirtualItems().map((item) => {
              const row = rows[item.index];
              if (!row) return null;
              return <div key={row.personId} className="absolute left-0 top-0 w-full px-2 py-1" style={{ transform: `translateY(${item.start}px)`, height: item.size }}>
                <div data-testid={`position-staffing-person-${row.personId}`} draggable={canvasDragEnabled && row.assignable && !controlsDisabled} onDragStart={(event) => {
                  nativePayloadRef.current = { kind: 'person', personId: row.personId };
                  event.dataTransfer.effectAllowed = 'move';
                  event.dataTransfer.setData('application/x-position-staffing', 'person');
                  usePositionStaffingDragStore.getState().begin({ kind: 'person', personId: row.personId }, { x: event.clientX, y: event.clientY }, 'native');
                }} onDragEnd={() => { nativePayloadRef.current = null; usePositionStaffingDragStore.getState().clear('native'); }} className="flex h-11 items-center gap-2 rounded-md border border-transparent px-2 hover:border-slate-200 hover:bg-slate-50">
                  <button type="button" className="flex h-10 w-10 shrink-0 touch-none items-center justify-center rounded text-slate-400 disabled:text-slate-300" disabled={controlsDisabled || !row.assignable} aria-label={`Drag ${row.displayLabel} to a position`} onPointerDown={(event) => {
                    if (controlsDisabled || !row.assignable) return;
                    event.preventDefault();
                    dragFocusRef.current = event.currentTarget;
                    event.currentTarget.setPointerCapture(event.pointerId);
                    usePositionStaffingDragStore.getState().begin({ kind: 'person', personId: row.personId }, { x: event.clientX, y: event.clientY }, event.pointerId);
                  }} onLostPointerCapture={(event) => {
                    const drag = usePositionStaffingDragStore.getState();
                    if (drag.pointerId !== event.pointerId) return;
                    drag.clear(event.pointerId);
                    setNotice({ state: 'idle', message: 'Staffing drag cancelled.' });
                    restoreFocus(dragFocusRef.current);
                    dragFocusRef.current = null;
                  }}><GripVertical className="h-4 w-4" aria-hidden /></button>
                  <button
                    type="button"
                    className="min-w-0 flex-1 rounded text-left outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    aria-label={row.primaryPositionId
                      ? `Select ${row.displayLabel} and focus their current position on the chart`
                      : `Select ${row.displayLabel}; they have no current position`}
                    onClick={() => {
                      const candidate = candidateByPersonId.get(row.personId);
                      if (candidate) selectCandidate(candidate);
                    }}
                  >
                    <div className="truncate text-xs font-medium text-slate-900">{row.displayLabel}</div>
                    {row.unassigned !== null && <div className="text-[10px] text-slate-500">{row.unassigned ? 'Unassigned' : `${row.assignmentCount} assignment${row.assignmentCount === 1 ? '' : 's'}`}</div>}
                  </button>
                  <button className="rounded bg-blue-600 px-2 py-1 text-[11px] text-white disabled:bg-slate-300" aria-label={`Assign ${row.displayLabel} to ${selectedGroup?.label ?? 'selected position'}`} disabled={controlsDisabled || !row.assignable || !targetPositionId || Boolean(targetState?.primaryOccupantPersonId)} onClick={() => targetPositionId && void runIntent({ kind: 'assign', personId: row.personId, targetPositionId, assignmentType: 'primary' })}>Assign</button>
                </div>
              </div>;
            })}
          </div>
        )}
      </div>
      {canvasDragEnabled && dragging?.kind === 'assignment' && <div data-position-staffing-unassign="true" data-testid="position-staffing-unassign-zone" className="fixed bottom-24 left-1/2 z-[90] -translate-x-1/2 rounded-lg border-2 border-dashed border-red-300 bg-red-50 px-8 py-4 text-sm font-medium text-red-800 shadow-lg">Drop here to unassign</div>}
      {canvasDragEnabled && dragging && dragPointer && <div className="pointer-events-none fixed z-[100] rounded-md bg-slate-900 px-2 py-1 text-xs text-white shadow-lg" style={{ left: dragPointer.x + 12, top: dragPointer.y + 12 }}>{dragMessage}</div>}
    </aside>
    </>
  );
};

const WorkbenchCandidateRow: React.FC<{
  candidate: PositionStaffingCandidate;
  selected: boolean;
  controlsDisabled: boolean;
  targetPositionLabel: string | null;
  targetBlocked: boolean;
  onSelect: () => void;
  onAssign: () => void;
}> = ({
  candidate,
  selected,
  controlsDisabled,
  targetPositionLabel,
  targetBlocked,
  onSelect,
  onAssign,
}) => (
  <div
    data-testid={`position-staffing-person-${candidate.row.personId}`}
    className={cn(
      'rounded-md border bg-white p-2 transition-colors',
      selected
        ? 'border-blue-300 bg-blue-50 shadow-sm'
        : 'border-transparent hover:border-slate-200',
    )}
  >
    <div className="flex items-start gap-2">
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        aria-label={candidate.currentPosition
          ? `Select ${candidate.row.displayLabel} and focus ${candidate.currentPosition.label} on the chart`
          : `Select ${candidate.row.displayLabel}; they have no current position`}
        className="min-w-0 flex-1 rounded text-left outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      >
        <div className="truncate text-xs font-medium text-slate-900">{candidate.row.displayLabel}</div>
        {/* `role` falls back to the person's imported title when they hold no
            position, so an enacted-away incumbent still read as "CEO" on the
            very surface used to choose who fills that seat. Say plainly when
            the title is source evidence rather than a current assignment. */}
        <div
          className="mt-0.5 truncate text-[10px] text-slate-500"
          title={candidate.currentPosition
            ? `Currently assigned to ${candidate.currentPosition.label}`
            : `Not assigned to a position. "${candidate.role}" comes from the imported source, not a current assignment.`}
        >
          {candidate.currentPosition ? candidate.role : `Unassigned · ${candidate.role}`}
        </div>
        <div className="mt-1 flex flex-wrap gap-1">
          {candidate.level !== 'Level not provided' ? (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-medium text-slate-600">{candidate.level}</span>
          ) : null}
          {candidate.department !== 'Department not provided' ? (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-medium text-slate-600">{candidate.department}</span>
          ) : null}
          {candidate.experience !== 'Experience not provided' ? (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-medium text-slate-600">{candidate.experience}</span>
          ) : null}
        </div>
      </button>
      <button
        type="button"
        className="shrink-0 rounded bg-blue-600 px-2 py-1 text-[11px] text-white disabled:bg-slate-300"
        aria-label={`Assign ${candidate.row.displayLabel} to ${targetPositionLabel ?? 'selected position'}`}
        disabled={controlsDisabled
          || !candidate.row.assignable
          || !targetPositionLabel
          || targetBlocked}
        onClick={onAssign}
      >
        Assign
      </button>
    </div>
  </div>
);

type KnownAssignmentType = Exclude<PositionAssignmentType, 'unknown'>;
type StaffingIntentWithoutContext =
  | { kind: 'assign'; personId: string; targetPositionId: string; assignmentType: KnownAssignmentType }
  | { kind: 'move'; sourceAssignmentId: string; targetPositionId: string; assignmentType?: KnownAssignmentType }
  | { kind: 'unassign'; sourceAssignmentId: string }
  | { kind: 'change_assignment_type'; sourceAssignmentId: string; assignmentType: KnownAssignmentType };
type StructureIntentWithoutContext =
  | { kind: 'create'; title: string; department?: string; level?: string; managerPositionId?: string | null }
  | { kind: 'edit'; positionId: string; title: string; department?: string; level?: string }
  | { kind: 'reparent'; positionId: string; managerPositionId: string | null }
  | { kind: 'add_seat'; sourcePositionId: string }
  | { kind: 'duplicate'; sourcePositionId: string; title?: string }
  | { kind: 'retire'; positionId: string; reportingPolicy: 'retire_all' | 'reparent_direct_reports' };
type StaffingHit = { positionId: string | null; body: boolean; unassign?: boolean };

function staffingHit(stage: Konva.Stage | null, clientX: number, clientY: number): StaffingHit {
  if (!stage) return { positionId: null, body: false };
  const bounds = stage.container().getBoundingClientRect();
  let target: Konva.Node | null = stage.getIntersection({ x: clientX - bounds.left, y: clientY - bounds.top });
  let body = false;
  let positionId: string | null = null;
  while (target) {
    if (target.hasName('position-card-body')) body = true;
    if (target.hasName('position-staffing-card')) {
      positionId = target.id() || null;
      break;
    }
    target = target.getParent();
  }
  return { positionId, body };
}

function dropErrorMessage(reason: 'outside_body' | 'incomplete' | 'read_only' | 'target_primary_occupied'): string {
  if (reason === 'outside_body') return 'Drop onto the occupant area of a position card.';
  if (reason === 'incomplete') return 'Staffing is disabled because occupancy data is incomplete.';
  if (reason === 'read_only') return 'You do not have permission to staff this chart.';
  return 'That position already has a primary occupant. Use an acting, interim, or shared assignment instead.';
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function scenarioCount(summary: Record<string, unknown>): number {
  for (const key of ['targetCount', 'changeCount', 'factCount']) {
    const value = summary[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return 1;
}

function shortRevision(value: string): string {
  return value.length <= 8 ? value : value.slice(0, 8);
}

function scenarioCategorySummary(summary: Record<string, unknown>): string {
  return [
    ['position', summary.positionCount],
    ['staffing', summary.staffingCount],
    ['reporting', summary.reportingCount],
    ['succession', summary.successionCount],
  ].flatMap(([label, value]) =>
    typeof value === 'number' && value > 0 ? [`${label} ${value}`] : []).join(' · ');
}

function impactCount(summary: Readonly<Record<string, number | null>>): number {
  return Object.values(summary).reduce<number>((total, value) => total + (value ?? 0), 0);
}
